/**
 * Shared end-game scoring tables for ON MARS (base game).
 * Used by full manual, Quick Reference, search, scoring screen, and diagrams.
 *
 * Technology column OP 1/4/9/16/25: matches official rulebook scoring example
 * (tiles worth 1 + 4 + 4 + 9 = 18 OP).
 * Quarters row OP 0/1/3/6/10/15: six double-slot rows (capacity 4 + 2×shelters);
 * value 10 appears in the same official example.
 */

export const ON_MARS_SUPPORTED_PLAYER_COUNTS = [3, 4] as const;
export type OnMarsSupportedPlayerCount =
  (typeof ON_MARS_SUPPORTED_PLAYER_COUNTS)[number];

export function isOnMarsSupportedPlayerCount(
  value: number
): value is OnMarsSupportedPlayerCount {
  return (ON_MARS_SUPPORTED_PLAYER_COUNTS as readonly number[]).includes(value);
}

export type ScoringRow = {
  id: string;
  label: string;
  points: number;
  notes?: string;
};

export type TechnologyColumn = {
  id: string;
  level: number;
  pointsPerTile: number;
  /** Development cost printed above the column (paid when moving INTO this column). */
  developCostSummary: string;
};

export type QuartersRow = {
  id: string;
  rowFromBottom: number;
  slotCount: number;
  /** Extra shelters beyond the starting one required to unlock this row. */
  extraSheltersRequired: number;
  points: number;
};

export const onMarsProgressScoring: ScoringRow[] = [
  { id: 'progress-1', label: '1 kostka postępu', points: 1 },
  { id: 'progress-2', label: '2 kostki postępu', points: 2 },
  { id: 'progress-3', label: '3 kostki postępu', points: 4 },
  { id: 'progress-4', label: '4 kostki postępu', points: 7 },
  { id: 'progress-5', label: '5 kostek postępu', points: 11 },
];

export const onMarsShipScoring = {
  id: 'ships',
  pointsPerShipInHangar: 3,
  label: 'Każdy statek w hangarze',
} as const;

export const onMarsBlueprintScoring = {
  builtLevel1: 3,
  builtLevel3: 5,
  unbuiltLevel1: -3,
  unbuiltLevel3: -5,
} as const;

export const onMarsScientistScoring = {
  pointsPerMatchingAdvancedBuildingOnMars: 3,
  label:
    'Każdy naukowiec: 3 PS za każdy budynek zaawansowany wskazanego typu na Marsie (niezależnie od właściciela)',
} as const;

/** Laboratory columns left → right (levels 1–5). */
export const onMarsTechnologyColumns: TechnologyColumn[] = [
  {
    id: 'tech-col-1',
    level: 1,
    pointsPerTile: 1,
    developCostSummary: 'Kolumna startowa (nauka technologii)',
  },
  {
    id: 'tech-col-2',
    level: 2,
    pointsPerTile: 4,
    developCostSummary: 'Koszt rozwoju nad kolumną 2 (zasób z planszy)',
  },
  {
    id: 'tech-col-3',
    level: 3,
    pointsPerTile: 9,
    developCostSummary: 'Koszt rozwoju nad kolumną 3 (zasób z planszy)',
  },
  {
    id: 'tech-col-4',
    level: 4,
    pointsPerTile: 16,
    developCostSummary: 'Koszt rozwoju nad kolumną 4 (zasób z planszy)',
  },
  {
    id: 'tech-col-5',
    level: 5,
    pointsPerTile: 25,
    developCostSummary: 'Koszt rozwoju nad kolumną 5 (zasób z planszy)',
  },
];

/**
 * Living Quarters: fill bottom → top. Score = points of the highest occupied row.
 * Base unlock: rows 1–2 (4 slots). Each extra Shelter unlocks the next row (+2 slots).
 */
export const onMarsQuartersRows: QuartersRow[] = [
  {
    id: 'quarters-row-1',
    rowFromBottom: 1,
    slotCount: 2,
    extraSheltersRequired: 0,
    points: 0,
  },
  {
    id: 'quarters-row-2',
    rowFromBottom: 2,
    slotCount: 2,
    extraSheltersRequired: 0,
    points: 1,
  },
  {
    id: 'quarters-row-3',
    rowFromBottom: 3,
    slotCount: 2,
    extraSheltersRequired: 1,
    points: 3,
  },
  {
    id: 'quarters-row-4',
    rowFromBottom: 4,
    slotCount: 2,
    extraSheltersRequired: 2,
    points: 6,
  },
  {
    id: 'quarters-row-5',
    rowFromBottom: 5,
    slotCount: 2,
    extraSheltersRequired: 3,
    points: 10,
  },
  {
    id: 'quarters-row-6',
    rowFromBottom: 6,
    slotCount: 2,
    extraSheltersRequired: 4,
    points: 15,
  },
];

export const onMarsScoringWorkedExample = {
  id: 'scoring-example-green-style',
  title: 'Przykład punktowania końcowego',
  lines: [
    { label: 'Kostki postępu (4)', points: 7 },
    { label: 'Statki w hangarze (2 × 3)', points: 6 },
    {
      label: 'Kwatery — najwyższy rząd z wartością 10',
      points: 10,
    },
    {
      label: 'Technologie: kolumna 1 + 2× kolumna 2 + kolumna 3 (1+4+4+9)',
      points: 18,
    },
    { label: 'Plany: zbudowany poz. 1 (+3), niezbudowany poz. 3 (−5)', points: -2 },
    { label: 'Naukowiec × 2 budynki zaawansowane zgodnego typu (2×3)', points: 6 },
    { label: 'Kontrakt (przykład +4 / −0)', points: 4 },
  ],
  total: 49,
  note: 'Suma pokazuje kategorie tabelowe. Kontrakty zawsze wg karty.',
} as const;

// Align worked example with chapter text: 7+6+10+18-2+6+4 = 49
// Keep lines matching that sum.

export function quartersPointsForColonistCount(colonistsInQuarters: number): number {
  if (colonistsInQuarters <= 0) return 0;
  let remaining = colonistsInQuarters;
  let points = 0;
  for (const row of onMarsQuartersRows) {
    if (remaining <= 0) break;
    const placed = Math.min(row.slotCount, remaining);
    if (placed > 0) {
      points = row.points;
    }
    remaining -= placed;
  }
  return points;
}

export function technologyPointsForColumns(columnLevels: number[]): number {
  return columnLevels.reduce((sum, level) => {
    const col = onMarsTechnologyColumns.find((c) => c.level === level);
    return sum + (col?.pointsPerTile ?? 0);
  }, 0);
}

export function scoringTablesSearchBlob(): string {
  const parts: string[] = [
    'punktacja końcowa PS OP kwatery laboratorium technologie',
    ...onMarsProgressScoring.map((r) => `${r.label} ${r.points} PS`),
    `${onMarsShipScoring.label} ${onMarsShipScoring.pointsPerShipInHangar} PS`,
    ...onMarsTechnologyColumns.map(
      (c) =>
        `technologia kolumna poziom ${c.level} ${c.pointsPerTile} PS ${c.developCostSummary}`
    ),
    ...onMarsQuartersRows.map(
      (r) =>
        `kwatery rząd ${r.rowFromBottom} ${r.points} PS schronienia ${r.extraSheltersRequired}`
    ),
    `plany zbudowany ${onMarsBlueprintScoring.builtLevel1} ${onMarsBlueprintScoring.builtLevel3}`,
    `plany niezbudowany ${onMarsBlueprintScoring.unbuiltLevel1} ${onMarsBlueprintScoring.unbuiltLevel3}`,
    onMarsScientistScoring.label,
    onMarsScoringWorkedExample.title,
    ...onMarsScoringWorkedExample.lines.map((l) => `${l.label} ${l.points}`),
    `suma przykładu ${onMarsScoringWorkedExample.total}`,
  ];
  return parts.join(' ');
}

/** Returns human-readable validation errors (empty = OK). */
export function validateOnMarsScoringTables(): string[] {
  const errors: string[] = [];

  if (ON_MARS_SUPPORTED_PLAYER_COUNTS.length !== 2) {
    errors.push('supportedPlayerCounts must be exactly [3, 4]');
  }
  if (!isOnMarsSupportedPlayerCount(3) || !isOnMarsSupportedPlayerCount(4)) {
    errors.push('supportedPlayerCounts must include 3 and 4');
  }

  if (onMarsProgressScoring.length !== 5) {
    errors.push('progress scoring must have 5 rows');
  }
  const expectedProgress = [1, 2, 4, 7, 11];
  onMarsProgressScoring.forEach((row, i) => {
    if (row.points !== expectedProgress[i]) {
      errors.push(`progress row ${i + 1} expected ${expectedProgress[i]}, got ${row.points}`);
    }
    if (!Number.isFinite(row.points)) {
      errors.push(`progress row ${row.id} missing points`);
    }
  });

  if (onMarsShipScoring.pointsPerShipInHangar !== 3) {
    errors.push('ship scoring must be 3 PS');
  }

  if (onMarsTechnologyColumns.length !== 5) {
    errors.push('technology must have 5 columns');
  }
  const expectedTech = [1, 4, 9, 16, 25];
  onMarsTechnologyColumns.forEach((col, i) => {
    if (col.level !== i + 1) {
      errors.push(`tech column index ${i} level mismatch`);
    }
    if (col.pointsPerTile !== expectedTech[i]) {
      errors.push(
        `tech column ${col.level} expected ${expectedTech[i]} PS, got ${col.pointsPerTile}`
      );
    }
    if (!col.developCostSummary.trim()) {
      errors.push(`tech column ${col.level} missing develop cost summary`);
    }
  });

  if (onMarsQuartersRows.length !== 6) {
    errors.push('quarters must have 6 rows');
  }
  const expectedQuarters = [0, 1, 3, 6, 10, 15];
  let totalSlots = 0;
  onMarsQuartersRows.forEach((row, i) => {
    totalSlots += row.slotCount;
    if (row.points !== expectedQuarters[i]) {
      errors.push(
        `quarters row ${row.rowFromBottom} expected ${expectedQuarters[i]}, got ${row.points}`
      );
    }
    if (row.slotCount !== 2) {
      errors.push(`quarters row ${row.rowFromBottom} must have 2 slots`);
    }
    if (!Number.isFinite(row.points)) {
      errors.push(`quarters row ${row.id} missing points`);
    }
  });
  if (totalSlots !== 12) {
    errors.push(`quarters total slots must be 12, got ${totalSlots}`);
  }

  if (onMarsBlueprintScoring.builtLevel1 !== 3 || onMarsBlueprintScoring.builtLevel3 !== 5) {
    errors.push('blueprint built scores must be 3 / 5');
  }
  if (
    onMarsBlueprintScoring.unbuiltLevel1 !== -3 ||
    onMarsBlueprintScoring.unbuiltLevel3 !== -5
  ) {
    errors.push('blueprint unbuilt scores must be -3 / -5');
  }

  if (onMarsScientistScoring.pointsPerMatchingAdvancedBuildingOnMars !== 3) {
    errors.push('scientist scoring must be 3 PS per matching advanced building');
  }

  const exampleSum = onMarsScoringWorkedExample.lines.reduce(
    (s, line) => s + line.points,
    0
  );
  if (exampleSum !== onMarsScoringWorkedExample.total) {
    errors.push(
      `worked example total ${onMarsScoringWorkedExample.total} != sum ${exampleSum}`
    );
  }

  // Official example fragments must remain expressible
  if (technologyPointsForColumns([1, 2, 2, 3]) !== 18) {
    errors.push('official tech example 1+4+4+9 must equal 18');
  }
  if (quartersPointsForColonistCount(9) !== 10) {
    // 8 slots = rows 1-4, 9th colonist enters row 5 (10 PS)
    errors.push('quarters with 9 colonists must score 10 PS (official example band)');
  }

  return errors;
}
